import { useEffect, useCallback, useRef } from "react";
import mapboxgl from "mapbox-gl";
import ReactDOMServer from "react-dom/server";
import { useSelector, useDispatch } from "react-redux";

import { useRpmPerState } from "@/components/home/useRpmPerState";
import { useIsDarkTheme } from "@/components/layout/useIsDarkTheme";
import { getUsaStates, getMarkets, getMarketCenters } from "@/state/mapDataSlice";
import { fitBounds } from "@/utils/map";

/**
 * Hook for managing market and state layers on a Mapbox map
 * @param {Object} options - Configuration options
 * @param {Object} options.mapRef - Reference to the mapbox map instance
 * @param {boolean} options.mapIsLoaded - Whether the map is loaded
 * @param {boolean} options.mapInitialised - Whether the map is initialised with layers
 * @param {Function} options.markInitialised - Callback to mark map as initialised
 * @param {Object} options.outbounds - Outbound data for market coloring
 * @param {Object} options.optimizePer - Optimization mode ({ id: "rpm" | "index" })
 * @param {number} options.nationalAverageRPM - National average RPM for color calculation
 * @param {Object} options.padding - Map padding
 * @param {React.Component} options.HoverPopupComponent - Component to render in hover popup
 * @returns {Object} Layer management functions and state
 */
export const useMarketLayers = ({
  mapRef,
  mapIsLoaded,
  mapInitialised,
  markInitialised,
  outbounds,
  optimizePer = { id: "rpm" },
  nationalAverageRPM,
  padding,
  HoverPopupComponent,
  isLocked = false,
}) => {
  const dispatch = useDispatch();
  const { markets, marketCenters, states } = useSelector((state) => state.mapData);
  const isDarkTheme = useIsDarkTheme();
  const { getColor, getIndexColor } = useRpmPerState();
  const popupRef = useRef(null);

  // Load map data if not already loaded
  useEffect(() => {
    if (!states.data) {
      dispatch(getUsaStates());
    }
    if (!markets.data) {
      dispatch(getMarkets());
    }
    if (!marketCenters.data) {
      dispatch(getMarketCenters());
    }
  }, []);

  // Initialize layers when map and data are ready
  useEffect(() => {
    if (!mapRef.current || !markets.data || !states.data || !mapIsLoaded || mapInitialised) {
      return;
    }

    const map = mapRef.current;

    // Add markets source
    map.addSource("markets", {
      type: "geojson",
      data: markets.data,
    });

    // Add markets fill layer (initially hidden)
    map.addLayer({
      id: "markets-layer",
      type: "fill",
      source: "markets",
      layout: {
        visibility: "none",
      },
      paint: {
        "fill-color": ["case", ["!=", ["get", "color"], null], ["get", "color"], isDarkTheme ? "#1E382E" : "#D9EDE4"],
        "fill-opacity": ["case", ["!=", ["get", "opacity"], null], ["get", "opacity"], 1],
      },
    });

    // Add hover layer
    map.addLayer({
      id: "hover-layer",
      type: "fill",
      source: "markets",
      paint: {
        "fill-color": isDarkTheme ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
      },
      filter: ["==", "marketName", ""],
    });

    // Add states source
    map.addSource("states", {
      type: "geojson",
      data: states.data,
    });

    // Add states border layer (initially hidden)
    map.addLayer({
      id: "states-border-layer",
      type: "line",
      source: "states",
      layout: {
        visibility: "none",
      },
      paint: {
        "line-color": isDarkTheme ? "#131414" : "#66857B",
        "line-width": 0.5,
        "line-opacity": 0.8,
      },
    });

    fitBounds(map, padding, 400);

    // Create popup
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: "custom-popup",
      maxWidth: "auto",
    });
    popupRef.current = popup;

    const clearHover = () => {
      map.setFilter("hover-layer", ["==", "marketName", ""]);
      popup.remove();
      map.getCanvas().style.cursor = "";
    };

    // Add hover events (only when not locked)
    if (!isLocked) {
      map.on("mousemove", "markets-layer", (event) => {
        const features = map.queryRenderedFeatures(event.point, {
          layers: ["markets-layer"],
        });

        if (features.length > 0) {
          const feature = features[0];
          const { marketName } = feature.properties;

          if (HoverPopupComponent) {
            const popupContent = ReactDOMServer.renderToString(<HoverPopupComponent outbound={feature.properties} />);
            map.setFilter("hover-layer", ["==", "marketName", marketName]);
            popup.setLngLat(event.lngLat).setHTML(popupContent).addTo(map);
          } else {
            map.setFilter("hover-layer", ["==", "marketName", marketName]);
          }
        } else {
          clearHover();
        }
      });

      map.on("mouseleave", "markets-layer", clearHover);
      map.on("mouseenter", "markets-layer", () => {
        map.getCanvas().style.cursor = "pointer";
      });
    }

    markInitialised();
  }, [markets.data, states.data, mapIsLoaded, mapInitialised, isDarkTheme, isLocked]);

  // Update market colors when outbounds data changes
  const updateMarketColors = useCallback(() => {
    if (!mapRef.current || !mapInitialised || !markets.data) return;

    const map = mapRef.current;
    const outboundsLength = outbounds ? Object.keys(outbounds).length : 0;

    // Check if map is still valid and style is loaded
    try {
      if (!map.isStyleLoaded()) return;
    } catch {
      return;
    }

    // If no outbounds, show skeleton layers
    if (outboundsLength === 0) {
      if (map.getLayer("skeleton-markets-layer")) {
        map.setLayoutProperty("skeleton-markets-layer", "visibility", "visible");
      }
      if (map.getLayer("skeleton-states-layer")) {
        map.setLayoutProperty("skeleton-states-layer", "visibility", "visible");
      }
      if (map.getLayer("markets-layer")) {
        map.setLayoutProperty("markets-layer", "visibility", "none");
      }
      if (map.getLayer("states-border-layer")) {
        map.setLayoutProperty("states-border-layer", "visibility", "none");
      }
      // Note: Don't remove selected-market-layer here - it's managed separately by PredictionMarketsMap
      return false; // Indicates skeleton is shown
    }

    if (!nationalAverageRPM) return false;

    // Update market colors
    const newMarketsData = JSON.parse(JSON.stringify(markets.data));
    newMarketsData.features.forEach((feature) => {
      const tempOut = outbounds[feature.properties.marketName];
      const outboundData = tempOut?.filtered || tempOut?.unfiltered;
      const rpm = outboundData?.rpm;
      const rate = outboundData?.rate;
      const confidence = outboundData?.confidence;
      const distance = outboundData?.distance;

      if (optimizePer.id === "rpm") {
        feature.properties.color = getColor(rpm || 0, nationalAverageRPM);
      } else {
        const sortedIndex = outboundData?.sortIndex;
        feature.properties.color = getIndexColor(sortedIndex, outboundsLength);
      }
      feature.properties.opacity = outboundData?.filtered ? 1 : 0.1;
      feature.properties.rpm = rpm || 0;
      feature.properties.rate = rate || 0;
      feature.properties.confidence = confidence || 0;
      feature.properties.distance = distance || 0;
    });

    const marketsSource = map.getSource("markets");
    if (!marketsSource) return false;
    marketsSource.setData(newMarketsData);

    // Hide skeleton layers and show actual layers
    if (map.getLayer("skeleton-markets-layer")) {
      map.setLayoutProperty("skeleton-markets-layer", "visibility", "none");
    }
    if (map.getLayer("skeleton-states-layer")) {
      map.setLayoutProperty("skeleton-states-layer", "visibility", "none");
    }
    if (map.getLayer("markets-layer")) {
      map.setLayoutProperty("markets-layer", "visibility", "visible");
    }
    if (map.getLayer("states-border-layer")) {
      map.setLayoutProperty("states-border-layer", "visibility", "visible");
    }

    return true; // Indicates data is shown
  }, [mapRef, mapInitialised, markets.data, outbounds, optimizePer, nationalAverageRPM, getColor, getIndexColor]);

  // Helper to move overlay layers to top (ensures they stay above other layers)
  const moveOverlayLayersToTop = useCallback((map) => {
    if (map.getLayer("radius-outside-overlay")) {
      map.moveLayer("radius-outside-overlay");
    }
    if (map.getLayer("radius-circle-outline")) {
      map.moveLayer("radius-circle-outline");
    }
    if (map.getLayer("pickup-market-toggles-layer")) {
      map.moveLayer("pickup-market-toggles-layer");
    }
  }, []);

  // Add selected market highlight layer
  const addSelectedMarketLayer = useCallback(
    (marketNames, color) => {
      if (!mapRef.current || !mapInitialised) return;

      const map = mapRef.current;

      if (map.getLayer("selected-market-layer")) {
        map.removeLayer("selected-market-layer");
      }

      if (!marketNames || marketNames.length === 0) return;

      const marketFilter = marketNames.length > 1 ? ["in", "marketName", ...marketNames] : ["==", "marketName", marketNames[0]];

      map.addLayer({
        id: "selected-market-layer",
        type: "line",
        source: "markets",
        paint: {
          "line-color": color || (isDarkTheme ? "#8EE4CA" : "#2E5D4B"),
          "line-width": 2,
          "line-opacity": 1,
        },
        filter: marketFilter,
      });

      // Ensure overlay layers stay on top
      moveOverlayLayersToTop(map);
    },
    [mapRef, mapInitialised, isDarkTheme, moveOverlayLayersToTop],
  );

  const removeSelectedMarketLayer = useCallback(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    if (map.getLayer("selected-market-layer")) {
      map.removeLayer("selected-market-layer");
    }
  }, [mapRef]);

  return {
    markets,
    marketCenters,
    states,
    isDarkTheme,
    updateMarketColors,
    addSelectedMarketLayer,
    removeSelectedMarketLayer,
    moveOverlayLayersToTop,
  };
};
